import { tr } from "./language";
/**
 * api.js — Central API client for the Voocly Partner Portal.
 *
 * Exports:
 *   - `api`            Axios instance (baseURL set, token injected by auth.js interceptor)
 *   - `parseApiError`  Extracts a structured error object from any axios catch
 *   - `API_BASE`       Raw base URL string (for legacy pages still using `const API = ...`)
 *
 * Backend error envelope (from GlobalExceptionHandler):
 *   { error: "error_code", detail: "Human message.", errors: { field: "msg" } | null }
 *
 * Error codes the backend sends:
 *   validation_error        → 400  (field-level errors in `errors` dict)
 *   bad_request             → 400  (generic bad input)
 *   invalid_request_body    → 400  (malformed JSON / wrong types)
 *   unauthenticated         → 401  (no / expired token)
 *   invalid_passcode        → 400  (OTP / passcode mismatch)
 *   forbidden               → 403  (authenticated but no permission)
 *   not_found               → 404
 *   conflict                → 409  (duplicate handle, already exists, etc.)
 *   internal_error          → 500
 */

import axios from "axios";
import { API_BASE, installAuthRefreshInterceptor } from "./sessionRefresh";

// ─── Axios instance ───────────────────────────────────────────────────────────
const api = axios.create({ baseURL: API_BASE });
api.defaults.withCredentials = true;
installAuthRefreshInterceptor(api);

export default api;

// ─── Error parser ─────────────────────────────────────────────────────────────

/**
 * parseApiError(err) → { code, message, errors, status }
 *
 * Use in every catch block instead of `err?.response?.data?.detail || "fallback"`.
 *
 * @param {unknown} err  The value thrown by axios
 * @param {string}  [fallback]  Message to use when we get no response at all
 * @returns {{
 *   code:    string,                      // backend error code, e.g. "conflict"
 *   message: string,                      // human-readable message to show the user
 *   errors:  Record<string,string> | null,// field-level errors for forms
 *   status:  number,                      // HTTP status code (0 = network error)
 * }}
 */
export function parseApiError(err, fallback = "Something went wrong. Please try again.") {
  if (!err?.response) {
    // Network error / timeout / CORS
    return { code: "network_error", message: tr("Unable to reach the server. Check your connection."), errors: null, status: 0 };
  }

  const { status, data } = err.response;
  const code    = data?.error   ?? httpCodeToSlug(status);
  const detail  = data?.detail;
  const message = safeErrorMessage(code, status, detail, fallback);
  const errors  = data?.errors  ?? null;

  return { code, message, errors, status };
}

/**
 * Friendly user-facing messages keyed by backend error code.
 * Pages can use this for toast / banner copy, or override per-context.
 */
export const ERROR_MESSAGES = {
  get validation_error() { return tr("Please fix the highlighted fields and try again."); },
  get bad_request() { return tr("The request was invalid. Please check your input."); },
  get invalid_request_body() { return tr("Some fields have incorrect values. Please review and resubmit."); },
  get unauthenticated() { return tr("Your session has expired. Please sign in again."); },
  get invalid_passcode() { return tr("Invalid passcode"); },
  get forbidden() { return tr("You don't have permission to do that."); },
  get not_found() { return tr("The requested resource was not found."); },
  get conflict() { return tr("This action conflicts with existing data (e.g. a duplicate)."); },
  internal_error:       "A server error occurred. Please try again later.",
  get network_error() { return tr("Unable to reach the server. Check your connection."); },
};

/** Returns the standard message for a given code, or the raw detail if unknown. */
export function friendlyError(err, fallback) {
  const { code, message } = parseApiError(err, fallback);
  return ERROR_MESSAGES[code] ?? message;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function httpCodeToSlug(status) {
  if (status === 400) return "bad_request";
  if (status === 401) return "unauthenticated";
  if (status === 403) return "forbidden";
  if (status === 404) return "not_found";
  if (status === 409) return "conflict";
  return "internal_error";
}

function safeErrorMessage(code, status, detail, fallback) {
  if (status >= 500 || code === "internal_error" || isTechnicalErrorDetail(detail)) {
    return ERROR_MESSAGES.internal_error;
  }

  return detail ?? fallback;
}

function isTechnicalErrorDetail(detail) {
  if (typeof detail !== "string") return false;

  return [
    /DbContext/i,
    /System\./i,
    /InvalidOperationException/i,
    /Stack trace/i,
    /at .+\(.+:\w+ \d+\)/i,
    /A second operation was started/i,
    /AWS_ACCESS_KEY_ID/i,
    /AWS_SECRET_ACCESS_KEY/i,
    /AWS_SESSION_TOKEN/i,
    /AWS credentials/i,
    /AmazonS3/i,
    /AWSSDK/i,
  ].some((pattern) => pattern.test(detail));
}
